/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/manager/schedule/scheduleShared.ts
 * PURPOSE: Helpers dùng chung màn Bảng phân ca — SC-009-SCHEDULE-GRID (WP-6b)
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-14
 * COMPLIANCE: SDLC 6.5.0 + Zero Mock Tolerance
 * PILLAR: P1 (Stage 04 - BUILD)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Manager — Xếp ca tuần (format ngày/giờ VI, bóc lỗi API)
 * - Impact: UX consistency — nhãn VI verbatim mockup
 * - Stakeholders: Branch Managers
 *
 * @technical-architecture
 * - Layer: Presentation Layer (pure helpers)
 * - Pattern: Pure functions, no side effects
 * - Dependencies: none
 *
 * @cross-references
 * - Page: frontend/src/pages/manager/ManagerSchedulePage.tsx
 * - Popover: frontend/src/pages/manager/schedule/ShiftCellPopover.tsx
 * - `weekStartOf` (2026-09-14, Rủi ro 1): mirror `_normalize_week_start()`
 *   backend/app/api/v2/endpoints/manager_schedule.py — dùng cho `touchedWeekStarts`
 *   ManagerSchedulePage.tsx (lưới N-ngày gộp nhiều tuần-máy-chủ).
 * ===============================================================================
 */

export const pad2 = (n: number) => String(n).padStart(2, "0");

/** Date → YYYY-MM-DD (local) */
export const toISODate = (d: Date) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** YYYY-MM-DD → Date (local, 00:00) */
export const parseISODate = (iso: string) => new Date(`${iso}T00:00:00`);

/** Thứ Hai của tuần chứa ngày d */
export const mondayOf = (d: Date) => {
  const copy = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  copy.setDate(copy.getDate() - ((copy.getDay() + 6) % 7));
  return copy;
};

/**
 * Đầu tuần THEO CẤU HÌNH TENANT chứa ngày d (2026-09-14, Rủi ro 1 — lưới
 * N-ngày gộp nhiều tuần-máy-chủ) — mirror CHÍNH XÁC `_normalize_week_start()`
 * bên backend (backend/app/api/v2/endpoints/manager_schedule.py, ISS-PILOT-16):
 * chấp nhận ngày bất kỳ trong tuần, neo về đúng ngày đầu tuần theo
 * `week_start_day` (1=Thứ 2…7=CN, khớp `ScheduleRulesInfo.week_start_day`).
 * `weekStartDay` mặc định 1 (Thứ 2) khớp `TenantWorkforceConfig.DEFAULT_WEEK_START_DAY`
 * — dùng cho khoảnh khắc trước khi client biết được cấu hình thật (xem
 * ManagerSchedulePage.tsx `touchedWeekStarts`).
 *
 * KHÔNG thay `mondayOf` (dùng ở nhiều nơi khác, hành vi Thứ-2-cố-định vẫn
 * đúng cho các chỗ đó) — hàm này chỉ CỘNG THÊM, dùng riêng cho chỗ cần đúng
 * ranh giới tuần-máy-chủ thật của tenant.
 */
export const weekStartOf = (d: Date, weekStartDay: number = 1) => {
  const copy = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  // JS getDay(): CN=0..T7=6 → quy đổi về weekday kiểu Python date.weekday()
  // (T2=0..CN=6, khớp công thức backend) trước khi tính khoảng lệch.
  const pyWeekday = (copy.getDay() + 6) % 7;
  const startWeekday = ((weekStartDay - 1) % 7 + 7) % 7; // 1..7 -> 0..6
  const daysSinceStart = ((pyWeekday - startWeekday) % 7 + 7) % 7;
  copy.setDate(copy.getDate() - daysSinceStart);
  return copy;
};

/** YYYY-MM-DD + n ngày → YYYY-MM-DD */
export const addDaysISO = (iso: string, days: number) => {
  const d = parseISODate(iso);
  d.setDate(d.getDate() + days);
  return toISODate(d);
};

/** YYYY-MM-DD → dd/mm */
export const formatDayMonth = (iso: string) => {
  const [, mm, dd] = iso.split("-");
  return `${dd}/${mm}`;
};

/** YYYY-MM-DD → dd/mm/yyyy */
export const formatDateVi = (iso: string) => {
  const [yyyy, mm, dd] = iso.split("-");
  return `${dd}/${mm}/${yyyy}`;
};

/** Khoảng tuần kiểu mockup: "28/07–03/08/2026" */
export const formatWeekRange = (weekStart: string, weekEnd: string) =>
  `${formatDayMonth(weekStart)}–${formatDateVi(weekEnd)}`;

/** Số giờ kiểu server _fmt_hours: 52 → "52" · 8.5 → "8.5" */
export const fmtHours = (value: number) =>
  Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100);

/** Bỏ dấu tiếng Việt để lọc combobox không cần gõ dấu (FIX-3) */
export const noDiacritics = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase();

/** Lỗi API đã bóc: message VI + (tuỳ) danh sách vi phạm từ 422 {message, violations} */
export interface ApiErrorInfo {
  message: string;
  violations?: string[];
}

export const extractApiError = (error: unknown): ApiErrorInfo => {
  // apiV2 (services/api/config.ts) wraps every non-401 error into an
  // AppError before it ever reaches here — the raw axios `error.response`
  // shape is gone, but the ORIGINAL response body (incl. `detail`) survives
  // on AppError's `context` field. Without this fallback, every violation
  // message from manager_schedule.py's "Chặn" mode (422 {message, violations})
  // was unreachable and this function always fell through to the generic
  // "Thao tác không thành công" string, regardless of what the backend said.
  const detail =
    (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail ??
    (error as { context?: { detail?: unknown } })?.context?.detail;
  if (typeof detail === "string") return { message: detail };
  if (detail && typeof detail === "object") {
    const obj = detail as { message?: unknown; violations?: unknown };
    const message =
      typeof obj.message === "string" ? obj.message : "Thao tác không thành công. Thử lại";
    const violations = Array.isArray(obj.violations)
      ? obj.violations
          .map((v) =>
            typeof v === "string"
              ? v
              : typeof (v as { message?: unknown })?.message === "string"
                ? (v as { message: string }).message
                : null,
          )
          .filter((v): v is string => v != null)
      : undefined;
    return { message, violations };
  }
  return { message: "Thao tác không thành công. Thử lại" };
};
